<template>
  <div class="modal fade" id="serviceDetailsModal" tabindex="-1">
    <div class="modal-dialog modal-lg">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">
            <i class="bx bx-info-circle me-2"></i>
            Service Details
          </h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
        </div>

        <div class="modal-body" v-if="service">
          <!-- Service Header -->
          <div class="row mb-4">
            <div class="col">
              <h4 class="mb-1">{{ service.service_name }}</h4>
              <div class="d-flex gap-2">
                <span class="badge bg-primary">{{ service.service_code }}</span>
                <span class="badge" :class="getCategoryBadgeClass(service.service_category)">
                  {{ service.service_category }}
                </span>
                <span class="badge" :class="service.is_active == 1 ? 'bg-success' : 'bg-danger'">
                  {{ service.is_active == 1 ? 'Active' : 'Inactive' }}
                </span>
              </div>
            </div>
            <div class="col-auto">
              <h3 class="text-primary mb-0">ETB {{ formatCurrency(service.unit_price) }}</h3>
              <small class="text-muted">Unit Price</small>
            </div>
          </div>

          <!-- Description -->
          <div v-if="service.description" class="mb-4">
            <h6 class="text-muted">Description</h6>
            <p>{{ service.description }}</p>
          </div>

          <!-- Medical Codes -->
          <div class="card mb-3">
            <div class="card-header bg-light">
              <h6 class="mb-0">Medical Codes</h6>
            </div>
            <div class="card-body">
              <div class="row">
                <div class="col-md-6 mb-2">
                  <small class="text-muted d-block">ICD-10 Code</small>
                  <span v-if="service.icd_code" class="fw-medium">{{ service.icd_code }}</span>
                  <span v-else class="text-muted">Not specified</span>
                </div>
                <div class="col-md-6 mb-2">
                  <small class="text-muted d-block">CPT Code</small>
                  <span v-if="service.cpt_code" class="fw-medium">{{ service.cpt_code }}</span>
                  <span v-else class="text-muted">Not specified</span>
                </div>
                <div class="col-md-6 mb-2">
                  <small class="text-muted d-block">SNOMED Code</small>
                  <span v-if="service.snomed_code" class="fw-medium">{{ service.snomed_code }}</span>
                  <span v-else class="text-muted">Not specified</span>
                </div>
                <div class="col-md-6 mb-2">
                  <small class="text-muted d-block">Local Code</small>
                  <span v-if="service.local_code" class="fw-medium">{{ service.local_code }}</span>
                  <span v-else class="text-muted">Not specified</span>
                </div>
              </div>
            </div>
          </div>

          <!-- CBHI Coverage -->
          <div class="card mb-3">
            <div class="card-header bg-light">
              <h6 class="mb-0">CBHI Coverage Information</h6>
            </div>
            <div class="card-body">
              <div class="row">
                <div class="col-md-6 mb-3">
                  <small class="text-muted d-block">Coverage Status</small>
                  <span class="badge" :class="getCoverageBadgeClass(service.cbhi_coverage_status)">
                    <i :class="getCoverageIcon(service.cbhi_coverage_status)"></i>
                    {{ formatCoverageStatus(service.cbhi_coverage_status) }}
                  </span>
                </div>
                <div class="col-md-6 mb-3" v-if="service.cbhi_copayment_percentage">
                  <small class="text-muted d-block">Patient Copayment</small>
                  <span class="fw-medium">{{ service.cbhi_copayment_percentage }}%</span>
                </div>
                <div class="col-md-6 mb-3" v-if="service.cbhi_coverage_limit">
                  <small class="text-muted d-block">Annual Coverage Limit</small>
                  <span class="fw-medium">ETB {{ formatCurrency(service.cbhi_coverage_limit) }}</span>
                </div>
                <div class="col-md-6 mb-3" v-if="service.per_visit_limit">
                  <small class="text-muted d-block">Per Visit Limit</small>
                  <span class="fw-medium">ETB {{ formatCurrency(service.per_visit_limit) }}</span>
                </div>
                <div class="col-md-6 mb-3" v-if="service.cbhi_frequency_limit">
                  <small class="text-muted d-block">Frequency Limit</small>
                  <span class="fw-medium">{{ service.cbhi_frequency_limit }} times per year</span>
                </div>
                <div class="col-12">
                  <div v-if="service.cbhi_preauth_required" class="alert alert-warning mb-2">
                    <i class="bx bx-shield-check me-2"></i>
                    Pre-authorization required
                  </div>
                  <div v-if="service.requires_referral" class="alert alert-info mb-0">
                    <i class="bx bx-user-check me-2"></i>
                    Referral required
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Metadata -->
          <div class="row mt-3 text-muted small">
            <div class="col-6">
              <i class="bx bx-calendar me-1"></i>
              Created: {{ formatDate(service.created_at) }}
            </div>
            <div class="col-6 text-end">
              <i class="bx bx-time me-1"></i>
              Last Updated: {{ formatDate(service.updated_at) }}
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">
            <i class="bx bx-x me-1"></i>Close
          </button>
          <button type="button" class="btn btn-primary" @click="editService">
            <i class="bx bx-edit me-1"></i>Edit Service
          </button>
          <button type="button" class="btn btn-warning" @click="manageCoverage">
            <i class="bx bx-shield me-1"></i>Manage Coverage
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import { authUrl, executeJson } from "@/api";

export default {
  name: "ServiceDetailsModal",
  props: {
    service: {
      type: Object,
      default: null
    }
  },

  data() {
    return {
      modal: null
    };
  },

  mounted() {
    this.modal = new bootstrap.Modal(document.getElementById('serviceDetailsModal'));
  },

  methods: {
    openModal() {
      this.modal.show();
    },

    closeModal() {
      this.modal.hide();
    },

    editService() {
      this.$emit('edit-service', this.service);
      this.closeModal();
    },

    manageCoverage() {
      this.$emit('manage-coverage', this.service);
      this.closeModal();
    },

    getCategoryBadgeClass(category) {
      const classMap = {
        'Consultation': 'bg-info',
        'Laboratory': 'bg-warning',
        'Radiology': 'bg-purple',
        'Procedure': 'bg-success',
        'Medication': 'bg-danger',
        'Room': 'bg-secondary',
        'Other': 'bg-dark'
      };
      return classMap[category] || 'bg-secondary';
    },

    getCoverageBadgeClass(status) {
      const classMap = {
        'fully_covered': 'bg-success',
        'partially_covered': 'bg-warning',
        'excluded': 'bg-danger',
        null: 'bg-secondary'
      };
      return classMap[status] || 'bg-secondary';
    },

    getCoverageIcon(status) {
      const iconMap = {
        'fully_covered': 'bx bx-check-circle me-1',
        'partially_covered': 'bx bx-adjust me-1',
        'excluded': 'bx bx-x-circle me-1',
        null: 'bx bx-question-mark me-1'
      };
      return iconMap[status] || 'bx bx-question-mark me-1';
    },

    formatCoverageStatus(status) {
      const statusMap = {
        'fully_covered': 'Fully Covered',
        'partially_covered': 'Partially Covered',
        'excluded': 'Excluded',
        null: 'Not Set'
      };
      return statusMap[status] || 'Unknown';
    },

    formatCurrency(amount) {
      return new Intl.NumberFormat().format(amount || 0);
    },

    formatDate(date) {
      if (!date) return '';
      const d = new Date(date);
      return d.toLocaleDateString('en-US', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    }
  }
};
</script>

<style scoped>
.bg-purple {
  background-color: #6f42c1 !important;
}
</style>